/**
 * PersonCard — shared card layout for any person entity (legislators, candidates, etc.).
 *
 * Provides a consistent structural skeleton with configurable content slots:
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │  [Avatar]  Name (party-styled link)  ★   [☑] [🔖]  │
 *   │            Chip · Chip · text · Chip                 │
 *   ├──────────────────────────────────────────────────────┤
 *   │  { badges }           (optional — e.g. workspace)    │
 *   │  { middleContent }    (optional — committees, etc.)  │
 *   │  ✉ email              (contact section)              │
 *   │  ☎ phone                                             │
 *   │  🌐 website                                          │
 *   │  { socialLinks }      (optional)                     │
 *   ├──────────────────────────────────────────────────────┤
 *   │  { statsRow }         (optional — bills, staff…)     │
 *   │  { footerRow }        (optional — position, etc.)    │
 *   └──────────────────────────────────────────────────────┘
 *
 * Consumers render entity-specific content via the slot props; the card
 * handles the surrounding structure, spacing, and interaction chrome.
 */

import React, { useState, useEffect } from 'react';
import { LucideIcon, Bookmark, Star, Mail, Phone, Globe, ExternalLink, Share2 } from 'lucide-react';
import Chip from './Chip';
import { getPartyChipVariant, getPartyTextColor } from '../utils/partyUtils';
import { formatPhoneNumber } from '../utils/nameUtils';

// =============================================================================
// Types
// =============================================================================

export type ChipVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'primary' | 'accent' | 'purple' | 'custom' | 'secondary' | 'outline';

export interface PersonCardChip {
  label: string;
  variant: ChipVariant;
  size?: 'xs' | 'sm' | 'md';
}

export interface PersonCardSocialLink {
  platform: string;
  label: string;
  url: string;
  title?: string;
}

export interface PersonCardProps {
  /** Unique key (used internally, not for React key — pass that on the outer grid) */
  id: string | number;

  // ── Name & identity ──────────────────────────────────────────────────────
  name: string;
  /** Party affiliation string — drives name color + party chip */
  party?: string | null;
  /** URL to navigate when the name is clicked */
  nameHref: string;
  /** react-router state to pass with navigation */
  nameLinkState?: Record<string, unknown>;
  /** Callback when name link is clicked (receives MouseEvent) */
  onNameClick?: (e: React.MouseEvent) => void;

  // ── Avatar ───────────────────────────────────────────────────────────────
  /** Photo URL — renders a rounded image when present */
  photoUrl?: string | null;
  /** Fallback icon shown when there is no photo */
  fallbackIcon: LucideIcon;

  // ── Chips row (below the name) ───────────────────────────────────────────
  chips?: PersonCardChip[];
  /** Content rendered between chips and the party chip (e.g. "HD-1" or a DistrictMapLink) */
  inlineText?: React.ReactNode;

  // ── Priority / selection / bookmark ──────────────────────────────────────
  isPriority?: boolean;
  isSelected?: boolean;
  isTracked?: boolean;
  onSelect?: (checked: boolean) => void;
  onBookmark?: () => void;
  bookmarkTitle?: string;

  // ── Contact info ─────────────────────────────────────────────────────────
  showContact?: boolean;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  socialLinks?: PersonCardSocialLink[];

  // ── Content slots ────────────────────────────────────────────────────────
  /** Rendered immediately after the header (e.g. WorkspaceBadgeList) */
  badges?: React.ReactNode;
  /** Rendered between badges and contact info (e.g. committees section) */
  middleContent?: React.ReactNode;
  /** Rendered in a border-top stats row at the bottom */
  statsRow?: React.ReactNode;
  /** Rendered below stats row (e.g. position indicator) */
  footerRow?: React.ReactNode;

  // ── Card interaction ─────────────────────────────────────────────────────
  onClick?: () => void;
  className?: string;
}

// =============================================================================
// Component
// =============================================================================

const PersonCard: React.FC<PersonCardProps> = ({
  name,
  party,
  nameHref: _nameHref,
  nameLinkState: _nameLinkState,
  onNameClick,
  photoUrl,
  fallbackIcon: FallbackIcon,
  chips = [],
  inlineText,
  isPriority,
  isSelected,
  isTracked,
  onSelect,
  onBookmark,
  bookmarkTitle,
  showContact = true,
  email,
  phone,
  website,
  socialLinks,
  badges,
  middleContent,
  statsRow,
  footerRow,
  onClick,
  className = '',
}) => {
  const partyColor = getPartyTextColor(party || undefined);
  const hasContact = showContact && (email || phone || website || (socialLinks && socialLinks.length > 0));
  const [imgFailed, setImgFailed] = useState(false);
  const showPhoto = photoUrl && !imgFailed;

  useEffect(() => {
    setImgFailed(false);
  }, [photoUrl]);

  return (
    <div
      className={`card p-4 hover:shadow-md transition-shadow cursor-pointer ${
        isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''
      } ${className}`}
      onClick={onClick}
    >
      {/* ── Header Row ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {/* Avatar */}
          {showPhoto ? (
            <img
              src={photoUrl}
              alt={name}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex-shrink-0">
              <FallbackIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
          )}

          {/* Name & chips */}
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNameClick?.(e);
                }}
                className={`${partyColor} font-semibold hover:underline cursor-pointer`}
              >
                {name}
              </button>
              {isPriority && (
                <Star className="w-4 h-4 text-yellow-500 fill-current flex-shrink-0" />
              )}
            </h3>

            {(chips.length > 0 || inlineText) && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {chips.map((chip, i) => (
                  <Chip key={i} label={chip.label} variant={chip.variant} size={chip.size || 'sm'} />
                ))}
                {inlineText && (
                  <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                    {inlineText}
                  </span>
                )}
                {party && (
                  <Chip label={party} variant={getPartyChipVariant(party)} size="sm" />
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Action icons (top-right) ──────────────────────────────── */}
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          {onSelect && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onSelect(e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
              className="rounded border-border-light dark:border-border-dark text-blue-600 focus:ring-blue-500"
            />
          )}

          {onBookmark && (
            <button
              className={`p-1 transition-colors ${
                isTracked
                  ? 'text-blue-600 hover:text-blue-700'
                  : 'text-text-tertiary hover:text-blue-600 dark:text-text-dark-tertiary'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onBookmark();
              }}
              title={bookmarkTitle || (isTracked ? 'Manage tracking' : 'Track')}
            >
              <Bookmark className="w-4 h-4" fill={isTracked ? 'currentColor' : 'none'} />
            </button>
          )}

          {!onBookmark && isTracked && (
            <Bookmark className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="currentColor" />
          )}
        </div>
      </div>

      {/* ── Badges slot ────────────────────────────────────────────── */}
      {badges}

      {/* ── Middle content slot ────────────────────────────────────── */}
      {middleContent}

      {/* ── Contact info ───────────────────────────────────────────── */}
      {hasContact && (
        <div className="space-y-2 mb-3">
          {email && (
            <a
              href={`mailto:${email}`}
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
              onClick={(e) => e.stopPropagation()}
            >
              <Mail className="w-4 h-4" />
              <span className="truncate">{email}</span>
            </a>
          )}
          {phone && (
            <a
              href={`tel:${phone}`}
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
              onClick={(e) => e.stopPropagation()}
            >
              <Phone className="w-4 h-4" />
              <span>{formatPhoneNumber(phone)}</span>
            </a>
          )}
          {website && (
            <a
              href={website.startsWith('http') ? website : `https://${website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
              onClick={(e) => e.stopPropagation()}
            >
              <Globe className="w-4 h-4" />
              <span className="truncate">{website.replace(/^https?:\/\//, '')}</span>
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
            </a>
          )}
          {socialLinks && socialLinks.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              {socialLinks.map((sl) => (
                <a
                  key={sl.platform}
                  href={sl.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
                  title={sl.title}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Share2 className="w-3.5 h-3.5" />
                  <span>{sl.label}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Stats row ──────────────────────────────────────────────── */}
      {statsRow && (
        <div className="flex items-center justify-between pt-3 border-t border-border-light dark:border-border-dark">
          {statsRow}
        </div>
      )}

      {/* ── Footer row ─────────────────────────────────────────────── */}
      {footerRow}
    </div>
  );
};

export default PersonCard;
